import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PricingBlock from "@/components/PricingBlock";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { ExternalLink, Send, RefreshCw, Check, XCircle, RotateCcw, Loader2 } from "lucide-react";

function InvoicePanel({ job, paymentStatus, hasInvoice, onStatusChange }) {
  const [sending, setSending] = useState(false);
  const [fetchingFee, setFetchingFee] = useState(false);

  const sendInvoice = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/square-invoice`);
      toast.success("Invoice sent to customer");
      if (onStatusChange) onStatusChange();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to send invoice";
      toast.error(msg);
    } finally { setSending(false); }
  };

  const resendInvoice = async () => {
    setSending(true);
    try {
      await api.post(`/jobs/${job.id}/square-invoice/remind`);
      toast.success("Invoice reminder sent");
      if (onStatusChange) onStatusChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
    } finally { setSending(false); }
  };

  const fetchSquareFee = async () => {
    setFetchingFee(true);
    try {
      await api.post(`/jobs/${job.id}/fetch-square-fee`);
      toast.success("Square fee retrieved");
      if (onStatusChange) onStatusChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fee not available from Square yet.");
    } finally { setFetchingFee(false); }
  };

  const showFetchFee = paymentStatus === "paid" && job.square_invoice_id && !job.square_processing_fee;

  const statusBadge = {
    invoice_sent: { label: "Invoice Sent", color: "bg-amber-500/15 text-amber-300 border-amber-500/40", icon: Send },
    paid: { label: "Paid", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40", icon: Check },
    voided: { label: "Invoice Voided", color: "bg-muted/30 text-muted-foreground border-border", icon: XCircle },
    refunded: { label: "Refunded", color: "bg-red-500/15 text-red-400 border-red-500/40", icon: RotateCcw },
  }[paymentStatus];

  return (
    <div data-testid="invoice-panel">
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-1.5">Invoice</div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          {statusBadge && (
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusBadge.color}`} data-testid="invoice-status-badge">
              <statusBadge.icon className="w-3 h-3" />
              {statusBadge.label}
            </span>
          )}
          {job.invoice_sent_at && (
            <span className="text-[10px] text-muted-foreground">
              {new Date(job.invoice_sent_at).toLocaleDateString()}
              {(job.invoice_reminder_count || 0) > 0 && ` · ${job.invoice_reminder_count}x`}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {(!paymentStatus || paymentStatus === "voided") && (
            <Button type="button" size="sm" variant="default" onClick={sendInvoice} disabled={sending} className="h-6 text-[10px] gap-1 px-2" data-testid="send-invoice-btn">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send
            </Button>
          )}
          {paymentStatus === "invoice_sent" && (
            <Button type="button" size="sm" variant="outline" onClick={resendInvoice} disabled={sending} className="h-6 text-[10px] gap-1 px-2 border-border" data-testid="resend-invoice-btn">
              {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Resend
            </Button>
          )}
          {hasInvoice && job.square_invoice_url && (
            <a href={job.square_invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline" data-testid="view-in-square-link">
              <ExternalLink className="w-3 h-3" /> Square
            </a>
          )}
          {showFetchFee && (
            <Button type="button" size="sm" variant="outline" onClick={fetchSquareFee} disabled={fetchingFee} className="h-6 text-[10px] gap-1 px-2 border-border" data-testid="fetch-square-fee-btn">
              {fetchingFee ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Fetch Fee
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function CurrentDateTime() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const dateStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  const timeStr = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
  const secStr = String(now.getSeconds()).padStart(2, "0");
  return (
    <div className="text-center">
      <div className="text-xs text-muted-foreground">{dateStr}</div>
      <div className="text-sm font-semibold text-foreground" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
        {timeStr}<span className="text-muted-foreground/50 text-xs">:{secStr}</span>
      </div>
    </div>
  );
}

// Mirrors backend services/geocoding.match_airport_address detection: keyword
// hit + street-indicator false-positive guard. Used to gate the optional
// airline/flight # fields in the JobDialog — ANY airport (broad), not fee-eligibility.
const _AIRPORT_KEYWORDS = /\b(jfk|lga|ewr|hpn|teb|isp|airport|terminal|intl|international)\b/i;
const _STREET_INDICATORS = /\b(blvd|street|st|ave|avenue|rd|road|drive|dr|lane|ln|way|place|pl|court|ct)\b/i;
function looksLikeAirport(addr) {
  const a = (addr || "").trim();
  if (!a) return false;
  if (_STREET_INDICATORS.test(a)) return false;
  return _AIRPORT_KEYWORDS.test(a);
}

// Port Authority fee eligibility — JFK / LGA / EWR only (bare codes + full
// names). Narrower than looksLikeAirport: HPN/TEB/ISP/private airports still
// show flight fields (looksLikeAirport) but are NOT PA-fee-eligible.
const _PA_FEE_KEYWORDS = /\b(jfk|lga|ewr)\b|\bjohn\s*f\.?\s*kennedy\b|\bla\s*guardia\b|\blaguardia\b|\bnewark\s*(liberty|international|intl)\b/i;
function isPaFeeAirport(addr) {
  const a = (addr || "").trim();
  if (!a) return false;
  if (_STREET_INDICATORS.test(a)) return false;
  return _PA_FEE_KEYWORDS.test(a);
}

// Out-of-state detection for NY sales tax exemption — a ride is tax-exempt
// when either leg is outside NY State. Newark/EWR kept as an explicit keyword
// (most common interstate case, may appear without a state suffix on saved
// landmarks); the abbreviation/full-name checks cover the general case for
// addresses that carry a state (Nominatim-picked addresses append the full
// state name). No state detected at all → default to taxed (NY); dispatcher
// can still manually flip the "Tax Exempt" pill.
const _NEWARK_KEYWORDS = /\b(ewr|newark)\b/i;
const _NON_NY_STATE_ABBR = /(?:,\s*|\s)(NJ|CT|PA|MA|RI|VT|DE|MD|NH|ME)\b(?:\s*\d{5})?\s*$/i;
const _NON_NY_STATE_NAMES = /\b(new\s*jersey|connecticut|pennsylvania|massachusetts|rhode\s*island|vermont|delaware|maryland|new\s*hampshire|maine)\b/i;
function isOutOfState(addr) {
  const a = (addr || "").trim();
  if (!a) return false;
  if (_NEWARK_KEYWORDS.test(a)) return true;
  return _NON_NY_STATE_ABBR.test(a) || _NON_NY_STATE_NAMES.test(a);
}

const _MANHATTAN_KEYWORDS = /\b(manhattan|midtown|downtown|soho|tribeca|chelsea|greenwich\s*village|east\s*village|west\s*village|upper\s*east|upper\s*west|lower\s*east|lower\s*manhattan|financial\s*district|wall\s*st|times\s*square|flatiron|gramercy|murray\s*hill|hell'?s?\s*kitchen|meatpacking|nolita|noho|little\s*italy|chinatown|battery\s*park|harlem|inwood|washington\s*heights|kips\s*bay|union\s*square|penn\s*station|grand\s*central)\b/i;
const _NON_MANHATTAN_BOROUGHS = /\b(brooklyn|queens|bronx|staten\s*island|astoria|flushing|williamsburg|bushwick|park\s*slope|bay\s*ridge|flatbush|crown\s*heights|long\s*island\s*city|jackson\s*heights|jamaica|riverdale|fordham|mott\s*haven)\b/i;
function detectCongestionZone(addr) {
  const a = (addr || "").trim();
  if (!a) return false;
  if (_MANHATTAN_KEYWORDS.test(a)) return true;
  if (/\bnew\s*york,\s*ny\b|\bnew\s*york\s+ny\b/i.test(a) && !_NON_MANHATTAN_BOROUGHS.test(a)) return true;
  return false;
}

export default function JobDialog({ open, onClose, onSaved, drivers = [], vehicles = [], job = null, defaultDate = null, readOnly = false, onEditRequest }) {
  const isEdit = !!job?.id && !readOnly;
  const showNotesPanel = !!(readOnly && job?.id);
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);

  useEffect(() => {
    if (!showNotesPanel) return;
    let cancelled = false;
    (async () => {
      setNotesLoading(true);
      try {
        const { data } = await api.get(`/jobs/${job.id}/notes`);
        if (cancelled) return;
        const sorted = [...(data?.notes || [])].sort(
          (a, b) => (b.created_at || "").localeCompare(a.created_at || "")
        );
        setNotes(sorted);
        if (sorted.some((n) => !n.read_at)) {
          api.post(`/jobs/${job.id}/notes/mark-read`).catch(() => {});
        }
      } catch {/* silent */}
      finally { if (!cancelled) setNotesLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [showNotesPanel, job?.id]);

  const [form, setForm] = useState({
    first_name: job?.first_name || (job?.client_name ? job.client_name.split(" ")[0] : ""),
    last_name: job?.last_name || (job?.client_name ? job.client_name.split(" ").slice(1).join(" ") : ""),
    client_phone: (() => {
      const p = job?.client_phone || "";
      if (p.startsWith("+") && p.includes(" ")) return p.split(" ").slice(1).join(" ");
      return p;
    })(),
    phone_code: (() => {
      const p = job?.client_phone || "";
      if (p.startsWith("+") && p.includes(" ")) return p.split(" ")[0];
      return "+1";
    })(),
    client_email: job?.client_email || "",
    account_number: job?.account_number || "",
    pickup_location: job?.pickup_location || "",
    dropoff_location: job?.dropoff_location || "",
    pickup_datetime: job?.pickup_datetime || defaultDate || "",
    passenger_count: job?.passenger_count || 1,
    luggage: job?.luggage || job?.luggage_count?.toString() || "",
    vehicle_class: job?.vehicle_class || job?.vehicle_type || "sedan",
    special_services: job?.special_services || [],
    special_instructions: job?.special_instructions || "",
    driver_number: job?.driver_number || "",
    driver_id: job?.driver_id || "",
    airline: job?.airline || "",
    flight_number: job?.flight_number || "",
    // Child seat details
    child_seats_rear: job?.child_seats_rear || 0,
    child_seats_front: job?.child_seats_front || 0,
    child_seats_booster: job?.child_seats_booster || 0,
    base_rate: job?.base_rate || "",
    tolls: job?.tolls || "",
    gratuity: job?.gratuity || "",
    driver_base_rate: job?.driver_base_rate || "",
    driver_gratuity: job?.driver_gratuity || "",
    ny_to_ny: job?.ny_to_ny !== undefined ? job.ny_to_ny : true,
    is_airport_trip: !!job?.is_airport_trip,
    is_congestion_zone: !!job?.is_congestion_zone,
    pricing_mode: job?.pricing_mode || "all_inclusive",
    all_inclusive_amount: job?.all_inclusive_amount || "",
    status: job?.status || "unassigned",
  });

  useEffect(() => {
    if (isEdit && job && !job.first_name && job.client_name) {
      const parts = job.client_name.split(" ");
      setForm((f) => ({ ...f, first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "" }));
    }
  }, [isEdit, job]);

  // Address-derived flag reconciliation: pickup + dropoff text is the source of truth
  // for PA Fee, CG Fee, and NY-to-NY. Runs on every address change.
  useEffect(() => {
    const pu = form.pickup_location || "";
    const doff = form.dropoff_location || "";

    // PA fee: JFK/LGA/EWR only (narrow). Flight-field visibility elsewhere in
    // this component still uses the broad looksLikeAirport check separately.
    const airportTrip = isPaFeeAirport(pu) || isPaFeeAirport(doff);
    const congestionZone = detectCongestionZone(pu) || detectCongestionZone(doff);
    // Tax exemption: either leg outside NY State → exempt. No state detected → taxed (safe default).
    const nyToNy = !(isOutOfState(pu) || isOutOfState(doff));

    setForm(prev => {
      if (prev.is_airport_trip === airportTrip &&
          prev.is_congestion_zone === congestionZone &&
          prev.ny_to_ny === nyToNy) {
        return prev;
      }
      return { ...prev, is_airport_trip: airportTrip, is_congestion_zone: congestionZone, ny_to_ny: nyToNy };
    });
  }, [form.pickup_location, form.dropoff_location]); // eslint-disable-line react-hooks/exhaustive-deps


  const [saving, setSaving] = useState(false);
  const [showChildSeats, setShowChildSeats] = useState(
    (job?.special_services || []).includes("child_seat")
  );
  const [driverLookup, setDriverLookup] = useState(null);
  const [driverLookupError, setDriverLookupError] = useState("");
  const [customerMatch, setCustomerMatch] = useState("");

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const updateFields = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  const lookupCustomer = async (field) => {
    if (job?.id) return; // skip on edit — already has customer data
    const params = {};
    if (field === "phone" && form.client_phone?.trim()) {
      params.phone = `${form.phone_code || "+1"} ${form.client_phone.trim()}`;
    } else if (field === "email" && form.client_email?.trim()) {
      params.email = form.client_email.trim();
    } else return;
    try {
      const { data } = await api.get("/jobs/customer-lookup", { params });
      const fill = {};
      if (!form.first_name && data.first_name) fill.first_name = data.first_name;
      if (!form.last_name && data.last_name) fill.last_name = data.last_name;
      if (!form.client_email && data.client_email) {
        // store only the email part
        fill.client_email = data.client_email;
      }
      if (!form.client_phone && data.client_phone) {
        // parse stored "+1 5551234567" back into code + number
        const stored = data.client_phone || "";
        if (stored.startsWith("+") && stored.includes(" ")) {
          fill.phone_code = stored.split(" ")[0];
          fill.client_phone = stored.split(" ").slice(1).join(" ");
        } else {
          fill.client_phone = stored;
        }
      }
      if (!form.account_number && data.account_number) fill.account_number = data.account_number;
      if (Object.keys(fill).length > 0) {
        setForm((prev) => ({ ...prev, ...fill }));
        const name = [data.first_name, data.last_name].filter(Boolean).join(" ");
        setCustomerMatch(`Returning — ${name}`);
      }
    } catch {
      // 404 = no match, silently ignore
    }
  };

  const toggleService = (key) => {
    const has = form.special_services.includes(key);
    const next = has ? form.special_services.filter((s) => s !== key) : [...form.special_services, key];
    setForm((prev) => ({ ...prev, special_services: next }));
    if (key === "child_seat") {
      if (has) {
        setShowChildSeats(false);
        setForm((prev) => ({ ...prev, child_seats_rear: 0, child_seats_front: 0, child_seats_booster: 0, special_services: next }));
      } else {
        setShowChildSeats(true);
      }
    }
  };

  const isMeetGreet = form.special_services.includes("meet_greet");

  const lookupDriver = async (num) => {
    if (!num || !num.trim()) { setDriverLookup(null); setDriverLookupError(""); updateField("driver_id", ""); return; }
    try {
      const { data } = await api.get(`/drivers/lookup/${num.trim()}`);
      setDriverLookup(data); setDriverLookupError(""); updateField("driver_id", data.driver.id);
    } catch { setDriverLookup(null); setDriverLookupError("Not found"); updateField("driver_id", ""); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pickup_location || !form.dropoff_location || !form.pickup_datetime) {
      toast.error("Pickup, dropoff, and date/time are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_name: `${form.first_name} ${form.last_name}`.trim(),
        client_phone: form.client_phone ? `${form.phone_code || "+1"} ${form.client_phone}`.trim() : "",
        passenger_count: parseInt(form.passenger_count) || 1,
        luggage_count: 0,
        base_rate: parseFloat(form.base_rate) || 0,
        tolls: parseFloat(form.tolls) || 0,
        gratuity: parseFloat(form.gratuity) || 0,
        driver_base_rate: parseFloat(form.driver_base_rate) || parseFloat(form.base_rate) || 0,
        driver_gratuity: parseFloat(form.driver_gratuity) || parseFloat(form.gratuity) || 0,
        all_inclusive_amount: parseFloat(form.all_inclusive_amount) || 0,
        driver_id: form.driver_id || "",
        // If pickup is no longer airport-like, clear flight info to avoid
        // orphaned data on a non-airport job.
        airline: looksLikeAirport(form.pickup_location) ? (form.airline || "").trim() : "",
        flight_number: looksLikeAirport(form.pickup_location) ? (form.flight_number || "").trim() : "",
      };
      delete payload.phone_code;
      delete payload.first_name;
      delete payload.last_name;
      if (payload.driver_id) payload.status = "assigned";
      if (isEdit) { await api.put(`/jobs/${job.id}`, payload); toast.success("Job updated"); }
      else { await api.post("/jobs", payload); toast.success("Job created"); }
      onSaved({ isEdit });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg || "").join(", ") : "Failed to save";
      toast.error(msg);
    }
    finally { setSaving(false); }
  };

  const driverConfirmText = driverLookup
    ? `${driverLookup.driver.driver_number} — ${driverLookup.driver.name}${driverLookup.vehicle ? ` (${driverLookup.vehicle.make} ${driverLookup.vehicle.model})` : ""}`
    : "";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-5xl max-h-[92vh] overflow-y-auto p-0">
        <DialogHeader className="px-5 pt-4 pb-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {readOnly ? "View Job" : isEdit ? "Edit Job" : "New Job"}
              {job?.job_number && (
                <span
                  className="ml-3 text-muted-foreground font-normal tracking-wider"
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  data-testid="job-dialog-job-number"
                >
                  #{job.job_number}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">Job details form</DialogDescription>
            <CurrentDateTime />
            <div className="flex items-center gap-1.5">
              <img src="/logo-mark.png" alt="Arrivo" className="w-3.5 h-3.5 object-contain" />
              <span className="text-sm font-light tracking-[0.25em] text-primary" style={{ fontFamily: "'Manrope', sans-serif" }}>ARRIVO</span>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} data-testid="job-form">
          <fieldset disabled={readOnly} className="contents">
          <div className="grid grid-cols-[0.8fr_1fr_308px] gap-0 border-t border-border mt-2">

            {/* ====== COLUMN 1: PASSENGER INFO ====== */}
            <div className="px-4 py-3 border-r border-border space-y-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Passenger</div>

              <div className="grid grid-cols-[3fr_4fr] gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Phone</Label>
                  <div className="flex gap-1">
                    <Input
                      value={form.phone_code || "+1"}
                      onChange={(e) => updateField("phone_code", e.target.value)}
                      className="bg-muted/20 border-border h-8 text-[11px] w-[38px] px-1 text-center shrink-0"
                      data-testid="job-phone-code"
                    />
                    <Input value={form.client_phone} onChange={(e) => updateField("client_phone", e.target.value)} onBlur={() => lookupCustomer("phone")} className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-client-phone" placeholder="Phone" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Email</Label>
                  <Input value={form.client_email} onChange={(e) => updateField("client_email", e.target.value)} onBlur={() => lookupCustomer("email")} className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-client-email" placeholder="Email" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">First Name</Label>
                  <Input value={form.first_name} onChange={(e) => updateField("first_name", e.target.value)} className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-first-name" placeholder="First" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Last Name</Label>
                  <Input value={form.last_name} onChange={(e) => updateField("last_name", e.target.value)} className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-last-name" placeholder="Last" />
                </div>
              </div>

              {/* Account / Riders / Luggage — same row */}
              {customerMatch && <p className="text-xs text-emerald-400 -mt-1" data-testid="customer-match">{customerMatch}</p>}
              <div className="grid grid-cols-[0.7fr_1.8fr_0.7fr] gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Riders</Label>
                  <Input
                    type="number" min={1} max={99}
                    value={form.passenger_count}
                    onChange={(e) => updateField("passenger_count", e.target.value)}
                    className="bg-muted/20 border-border h-8 text-[11px] px-1.5"
                    data-testid="job-riders"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Luggage</Label>
                  <Input
                    value={form.luggage}
                    onChange={(e) => updateField("luggage", e.target.value)}
                    className="bg-muted/20 border-border h-8 text-[11px] px-1.5"
                    data-testid="job-luggage"
                    placeholder="e.g. 4 + 2 stroller"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Account</Label>
                  <Input value={form.account_number} onChange={(e) => updateField("account_number", e.target.value)} className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-account-number" placeholder="Acct" />
                </div>
              </div>

              {/* Special Services */}
              <div className="space-y-2">
                <Label className="text-[11px] text-muted-foreground">Services</Label>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => toggleService("meet_greet")}
                    className={`flex-1 flex items-center justify-center px-2 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                      isMeetGreet
                        ? "bg-primary/20 text-primary border border-primary/50"
                        : "bg-muted/20 text-muted-foreground border border-border hover:bg-muted/40"
                    }`}
                    data-testid="service-meet_greet"
                  >
                    Meet & Greet
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleService("child_seat")}
                    className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                      form.special_services.includes("child_seat")
                        ? "bg-orange-400/15 text-orange-300 border border-orange-400/40"
                        : "bg-muted/20 text-muted-foreground border border-border hover:bg-muted/40"
                    }`}
                    data-testid="service-child_seat"
                  >
                    Child Seat
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleService("pet_friendly")}
                    className={`flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-all ${
                      form.special_services.includes("pet_friendly")
                        ? "bg-orange-400/15 text-orange-300 border border-orange-400/40"
                        : "bg-muted/20 text-muted-foreground border border-border hover:bg-muted/40"
                    }`}
                    data-testid="service-pet_friendly"
                  >
                    Pet Friendly
                  </button>
                </div>

                {showChildSeats && (
                  <div className="rounded-md border border-orange-400/30 bg-orange-400/5 p-2" data-testid="child-seat-details">
                    <div className="grid grid-cols-3 gap-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Rear-facing</Label>
                        <Input
                          type="number" min={0} max={9}
                          value={form.child_seats_rear}
                          onChange={(e) => updateField("child_seats_rear", parseInt(e.target.value) || 0)}
                          className="bg-muted/20 border-border h-7 text-[11px] text-center px-1.5"
                          data-testid="child-seat-rear"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Front-facing</Label>
                        <Input
                          type="number" min={0} max={9}
                          value={form.child_seats_front}
                          onChange={(e) => updateField("child_seats_front", parseInt(e.target.value) || 0)}
                          className="bg-muted/20 border-border h-7 text-[11px] text-center px-1.5"
                          data-testid="child-seat-front"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[10px] text-muted-foreground">Booster</Label>
                        <Input
                          type="number" min={0} max={9}
                          value={form.child_seats_booster}
                          onChange={(e) => updateField("child_seats_booster", parseInt(e.target.value) || 0)}
                          className="bg-muted/20 border-border h-7 text-[11px] text-center px-1.5"
                          data-testid="child-seat-booster"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ====== COLUMN 2: TRIP DETAILS ====== */}
            <div className="px-4 py-3 border-r border-border space-y-3">
              <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-2">Trip Details</div>

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Pickup *</Label>
                <AddressAutocomplete
                  value={form.pickup_location}
                  onChange={(v) => updateField("pickup_location", v)}
                  placeholder=""
                  className="bg-muted/20 border-border h-8 text-[11px] px-1.5"
                  testId="job-pickup-location"
                />
              </div>
              {looksLikeAirport(form.pickup_location) && (
                <div className="grid grid-cols-2 gap-2 pl-3 border-l-2 border-emerald-500/30 ml-1">
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Airline</Label>
                    <Input
                      value={form.airline}
                      onChange={(e) => updateField("airline", e.target.value)}
                      placeholder="Delta"
                      className="bg-muted/20 border-border h-8 text-[11px] px-1.5"
                      data-testid="job-airline"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[11px] text-muted-foreground">Flight #</Label>
                    <Input
                      value={form.flight_number}
                      onChange={(e) => updateField("flight_number", e.target.value)}
                      placeholder="DL1234"
                      className="bg-muted/20 border-border h-8 text-[11px] px-1.5"
                      data-testid="job-flight-number"
                    />
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Dropoff *</Label>
                <AddressAutocomplete
                  value={form.dropoff_location}
                  onChange={(v) => updateField("dropoff_location", v)}
                  onAutoFlags={(flags) => updateFields(flags)}
                  placeholder=""
                  className="bg-muted/20 border-border h-8 text-[11px] px-1.5"
                  testId="job-dropoff-location"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Date *</Label>
                  <Input
                    type="date"
                    value={form.pickup_datetime ? form.pickup_datetime.split("T")[0] : ""}
                    onChange={(e) => {
                      const time = form.pickup_datetime ? form.pickup_datetime.split("T")[1] || "09:00" : "09:00";
                      updateField("pickup_datetime", `${e.target.value}T${time}`);
                    }}
                    className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-pickup-date" required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Time *</Label>
                  <Input
                    type="time"
                    value={form.pickup_datetime ? form.pickup_datetime.split("T")[1] || "" : ""}
                    onChange={(e) => {
                      const date = form.pickup_datetime ? form.pickup_datetime.split("T")[0] : "";
                      updateField("pickup_datetime", `${date}T${e.target.value}`);
                    }}
                    className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-pickup-time" required
                  />
                </div>
              </div>

              {/* Vehicle Class + Driver # — same row */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Vehicle Class</Label>
                  <Select value={form.vehicle_class} onValueChange={(v) => updateField("vehicle_class", v)}>
                    <SelectTrigger className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-vehicle-class"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sedan">Sedan</SelectItem>
                      <SelectItem value="suv">SUV</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Driver #</Label>
                  <Input
                    value={form.driver_number}
                    onChange={(e) => { updateField("driver_number", e.target.value); setDriverLookup(null); setDriverLookupError(""); }}
                    onBlur={() => lookupDriver(form.driver_number)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); lookupDriver(form.driver_number); } }}
                    className="bg-muted/20 border-border h-8 text-[11px] px-1.5" data-testid="job-driver-number" placeholder="Driver #"
                  />
                </div>
              </div>
              {driverConfirmText && <p className="text-xs text-emerald-400 -mt-2" data-testid="driver-confirm">{driverConfirmText}</p>}
              {driverLookupError && <p className="text-xs text-red-400 -mt-2" data-testid="driver-error">{driverLookupError}</p>}

              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground">Special Instructions</Label>
                <Textarea value={form.special_instructions} onChange={(e) => updateField("special_instructions", e.target.value)} className="bg-muted/20 border-border resize-none text-[11px] px-1.5" rows={2} data-testid="job-instructions" />
              </div>
            </div>

            {/* ====== COLUMN 3: PRICING ====== */}
            <div className="px-4 py-3 flex flex-col">
              <PricingBlock
                form={form}
                updateFields={updateFields}
                invoiceLocked={isEdit && ["invoice_sent", "paid"].includes(job?.payment_status)}
                squareFee={job?.square_processing_fee || 0}
              />
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(() => {
                  const taxExempt = !form.ny_to_ny;
                  const isAllInclusive = form.pricing_mode === "all_inclusive";
                  const fareLocked = isEdit && ["invoice_sent", "paid"].includes(job?.payment_status);
                  return (
                    <>
                      {form.pricing_mode !== "no_show" && (
                        <button
                          type="button"
                          disabled={fareLocked}
                          onClick={() => !fareLocked && updateFields({ pricing_mode: isAllInclusive ? "line_items" : "all_inclusive" })}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                            isAllInclusive
                              ? "bg-primary/20 text-primary border border-primary/50"
                              : "border border-border text-muted-foreground hover:bg-muted/30"
                          } ${fareLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                          data-testid="flag-all-inclusive"
                        >All-Inclusive</button>
                      )}
                      {form.pricing_mode === "no_show" && (
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold text-red-500 border border-red-500/40 bg-red-500/10 tracking-wider" data-testid="flag-no-show">NO-SHOW</span>
                      )}
                      <button
                        type="button"
                        disabled={fareLocked}
                        onClick={() => !fareLocked && updateFields({ ny_to_ny: !form.ny_to_ny })}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                          taxExempt
                            ? "bg-amber-400/20 text-amber-300 border border-amber-400/50"
                            : "border border-border text-muted-foreground hover:bg-muted/30"
                        } ${fareLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-testid="flag-tax-exempt"
                      >Tax Exempt</button>
                      <button
                        type="button"
                        disabled={fareLocked}
                        onClick={() => !fareLocked && updateFields({ is_airport_trip: !form.is_airport_trip })}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                          form.is_airport_trip
                            ? "bg-primary/20 text-primary border border-primary/50"
                            : "border border-border text-muted-foreground hover:bg-muted/30"
                        } ${fareLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-testid="flag-pa-fee"
                      >PA Fee</button>
                      <button
                        type="button"
                        disabled={fareLocked || taxExempt}
                        onClick={() => !fareLocked && !taxExempt && updateFields({ is_congestion_zone: !form.is_congestion_zone })}
                        title={taxExempt ? "Congestion fee does not apply to interstate rides" : undefined}
                        className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                          form.is_congestion_zone && !taxExempt
                            ? "bg-primary/20 text-primary border border-primary/50"
                            : "border border-border text-muted-foreground hover:bg-muted/30"
                        } ${fareLocked || taxExempt ? "opacity-50 cursor-not-allowed" : ""}`}
                        data-testid="flag-cg-fee"
                      >CG Fee</button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
          </fieldset>

          {/* ====== GPS · NOTES · INVOICE — single row (view mode) ====== */}
          {showNotesPanel && (() => {
            const status = job?.status || "";
            const gatedStates = ["arrived_at_pickup", "passenger_on_board", "completed", "closed", "cancelled_no_show", "unable_to_complete", "no_show"];
            const showGps = gatedStates.includes(status);
            const ec = job?.event_coordinates || {};
            const pickupStatus = ec.pickup?.status || "missing";
            const dropoffStatus = ec.dropoff?.status || "missing";
            const colorFor = (s) => s === "captured" ? "text-green-500" : s === "fallback" ? "text-amber-500" : "text-red-500";
            const labelFor = (s) => s === "captured" ? "Captured" : s === "fallback" ? "Fallback" : "Missing";

            const ps = job.payment_status;
            const hasInvoice = !!job.square_invoice_id;
            const showInvoice = readOnly && job?.id && (ps || (job.total > 0 && job.client_email));

            return (
              <div className="border-t border-border bg-muted/10 grid grid-cols-[auto_1fr_minmax(220px,auto)] divide-x divide-border" data-testid="job-info-row">
                {/* GPS */}
                {showGps ? (
                  <div className="px-4 py-2.5 min-w-[130px]" data-testid="gps-data-panel">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-1.5">GPS</div>
                    <div className="space-y-0.5 text-xs">
                      <div className="flex items-center gap-2" data-testid="gps-data-pickup-row">
                        <span className="w-12 text-muted-foreground">Pickup</span>
                        <span className={`font-medium ${colorFor(pickupStatus)}`} data-testid={`gps-data-pickup-${pickupStatus}`}>{labelFor(pickupStatus)}</span>
                      </div>
                      <div className="flex items-center gap-2" data-testid="gps-data-dropoff-row">
                        <span className="w-12 text-muted-foreground">Dropoff</span>
                        <span className={`font-medium ${colorFor(dropoffStatus)}`} data-testid={`gps-data-dropoff-${dropoffStatus}`}>{labelFor(dropoffStatus)}</span>
                      </div>
                    </div>
                  </div>
                ) : <div className="px-4 py-2.5 min-w-[130px]" />}

                {/* Driver Notes */}
                <div className="px-4 py-2.5" data-testid="driver-notes-panel">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-medium mb-1.5">
                    Driver Notes {notes.length > 0 && <span className="text-muted-foreground/60">({notes.length})</span>}
                  </div>
                  {notesLoading ? (
                    <div className="text-xs text-muted-foreground">Loading…</div>
                  ) : notes.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No notes.</div>
                  ) : (
                    <ul className="space-y-1 max-h-24 overflow-y-auto" data-testid="driver-notes-list">
                      {notes.map((n, idx) => (
                        <li key={n.id || idx} className="text-xs leading-snug border-l-2 border-amber-400/60 pl-2 py-0.5" data-testid="driver-note-item">
                          <div className="text-foreground whitespace-pre-wrap">{n.body}</div>
                          <div className="text-[10px] text-muted-foreground/70">{n.created_at ? new Date(n.created_at).toLocaleString() : ""}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Invoice Status */}
                {showInvoice ? (
                  <div className="px-4 py-2.5 min-w-[180px]">
                    <InvoicePanel
                      job={job}
                      paymentStatus={ps}
                      hasInvoice={hasInvoice}
                      onStatusChange={() => { if (onSaved) onSaved({ isEdit: true }); }}
                    />
                  </div>
                ) : <div />}
              </div>
            );
          })()}

          <DialogFooter className="px-5 py-3 border-t border-border">
            {readOnly ? (
              <>
                <Button type="button" variant="ghost" onClick={onClose} data-testid="job-close-button">Close</Button>
                {onEditRequest && <Button type="button" onClick={onEditRequest} data-testid="job-edit-from-view">Edit</Button>}
              </>
            ) : (
              <>
                <Button type="button" variant="ghost" onClick={onClose} data-testid="job-cancel-button">Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="job-save-button">{saving ? "Saving..." : isEdit ? "Update Job" : "Create Job"}</Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
